import { Component, Input, OnDestroy } from '@angular/core';

import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { ShiftBlockOption } from '../../models/shiftblock-option.model';
import { ShiftBlockType, ShiftSchedule } from 'src/shared/models/shift-schedule.model';
import { ShiftScheduleService } from 'src/shared/services/shift-schedule.service';
import { StaffingGridConstants, StaffingGridType } from '../../utilities/staffing-grid-constants.util';
import { StaffingGridUtil } from '../../utilities/staffing-grid.util';
import { StartHour } from '../../models/start-hour.model';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';
import { Constants } from 'src/shared/utilities/constants.util';

@Component({
    selector: 'smt-grid-input-variable-shiftblocks',
    templateUrl: './variable-shiftblocks.component.html',
    styleUrls: ['./variable-shiftblocks.component.scss']
})
export class VariableShiftblocksComponent implements OnDestroy {
    @Input() costCenter: CostCenter;
    @Input() readOnly: boolean;

    readonly hoursInDay = 24;
    readonly selectOptionDefaultValue: number = -999;
    readonly AllWeekType: ShiftBlockOption = { description: "All Week", id: ShiftBlockType.AllWeek };
    readonly WeekdayType: ShiftBlockOption = { description: "Weekday", id: ShiftBlockType.Weekday };
    readonly WeekendType: ShiftBlockOption = { description: "Weekend", id: ShiftBlockType.Weekend };
    readonly SaturdayOnlyType: ShiftBlockOption = { description: "Saturday Only", id: ShiftBlockType.Saturday };
    readonly SundayOnlyType: ShiftBlockOption = { description: "Sunday Only", id: ShiftBlockType.Sunday };
    readonly SelectShiftBlockType: ShiftBlockOption = {description: "Select", id: this.selectOptionDefaultValue };

    shiftBlockOptions = StaffingGridConstants.ShiftBlockOptions;
    shiftBlockHours = StaffingGridConstants.shiftBlockHours;

    shiftBlockSizes: ((string | number))[] = [ '-', 1, 2, 4, 6, 8, 12, 24 ];

    selectedShiftBlockOption: number;
    selectedBlockSize: (string|number);
    selectedStartTime: number;
    selectedEndTime: number;
    selectedClosedSchedule = false;
    selectedShiftBlockType: ShiftBlockType;
    selectedGridType: StaffingGridType = StaffingGridType.None;
    shiftSchedules = [] as ShiftSchedule[];

    private gridButtonPressedSubscription: Subscription;
    private variableShiftBlocksInputSubscription: Subscription;
    private addShiftSubject: Subject<any> = new Subject();

    constructor(private _costCenterConfigurationService: CostCenterConfigurationService, private _shiftScheduleService: ShiftScheduleService) { 
        this.variableShiftBlocksInputSubscription = this._shiftScheduleService.schedules$.subscribe((schedules) => {
            this.shiftSchedules = schedules;
        });

        this.gridButtonPressedSubscription = this._costCenterConfigurationService.gridButtonPressed$.subscribe((gridType) => {
            this.selectedGridType = gridType;
        });

        this.addShiftSubject
        .pipe(debounceTime(Constants.DebounceTime))
        .subscribe(async () => {
            await this.addShiftSchedule();
            }
        );
    }

    ngOnDestroy() {
        if (this.gridButtonPressedSubscription) {
            this.gridButtonPressedSubscription.unsubscribe();
        }

        if (this.variableShiftBlocksInputSubscription) {
            this.variableShiftBlocksInputSubscription.unsubscribe();
        }
    }

    get activeShiftSchedules() : ShiftSchedule[] {
        if (!this.shiftSchedules) {
             return [];
        }
    
        return this.shiftSchedules.filter(s => !s.inactiveDate);
    }

    get addButtonEnabled() : boolean {
        if (this.selectedShiftBlockOption < 0 || this.conflictingSchedule) {
            return false;
        }
        
        if (this.selectedClosedSchedule) {
            return true;
        }
    
        return (this.selectedEndTime === null || this.selectedEndTime >= 0) && 
            this.selectedStartTime >= 0 && 
            this.selectedBlockSize == +this.selectedBlockSize;
    }

    addShiftScheduleWithDebounce() {
        this.addShiftSubject.next();
    }

    async addShiftSchedule() {
        this._costCenterConfigurationService.announceAdminActionLoading(true);
        
        if (this.addButtonEnabled) {
            let addedSchedule = new ShiftSchedule;

            addedSchedule.blockLength = Math.max(0, +this.selectedBlockSize || 0);
            addedSchedule.costCenterId = this.costCenter.id;
            addedSchedule.createdDate = new Date();
            addedSchedule.endHour = this.selectedEndTime === null ? null : +this.selectedEndTime;
            addedSchedule.isClosed = this.selectedClosedSchedule;
            addedSchedule.isSaturday = +this.selectedShiftBlockOption === ShiftBlockType.Saturday;
            addedSchedule.isSunday = +this.selectedShiftBlockOption === ShiftBlockType.Sunday;
            addedSchedule.isWeekday = +this.selectedShiftBlockOption === ShiftBlockType.Weekday || +this.selectedShiftBlockOption === ShiftBlockType.AllWeek;
            addedSchedule.isWeekend = +this.selectedShiftBlockOption === ShiftBlockType.Weekend || +this.selectedShiftBlockOption === ShiftBlockType.AllWeek;
            addedSchedule.partialShiftBlocks = [];            
            addedSchedule.shiftBlockType = +this.selectedShiftBlockOption;
            addedSchedule.staffRatios = [];
            addedSchedule.startHour = Math.max(0, +this.selectedStartTime || 0);     
            
            // Shift blocks must be set after the start hour otherwise they will not save appropriately.
            addedSchedule.shiftBlocks = StaffingGridUtil.setPartialShiftBlocks(addedSchedule);
            
            // Save the new shift schedule.
            await this._shiftScheduleService.setShiftSchedules([ addedSchedule ]);

            // Get the saved shift schedule so we don't treat it as new later down the road.
            let updatedSchedules = await this._shiftScheduleService.getActiveShiftSchedulesByCostCenter(this.costCenter.id);

            // Find the schedule that isn't already apart of the existing schedules.  This will be the one that was just saved.
            let updatedSchedule = updatedSchedules.find(s => !this.shiftSchedules.some(ss => ss.id === s.id));

            // Do not overwrite the shift schedules, only add the new one.  This prevents user changes from being lost.
            if (updatedSchedule && updatedSchedule.id) {
                this.shiftSchedules.push(updatedSchedule);
            }
                        
            this._shiftScheduleService.announceSchedules(this.shiftSchedules);     
        }
    
        this.resetShiftBlockOptions();

        this._costCenterConfigurationService.announceAdminActionLoading(false);
        return false;
    }

    get canCloseSelectedShiftBlockType() : boolean {
        return this.selectedShiftBlockOption != ShiftBlockType.Weekday 
            && this.selectedShiftBlockOption != ShiftBlockType.AllWeek;
    }

    get conflictingSchedule() : boolean {
        switch (+this.selectedShiftBlockOption) {
            case ShiftBlockType.AllWeek:
                return this.activeShiftSchedules.length > 0
            case ShiftBlockType.Weekday:
                return !!this.activeShiftSchedules.find(s => s.isWeekday);
            case ShiftBlockType.Weekend:
                return !!this.activeShiftSchedules.find(s => s.isWeekend || s.isSaturday || s.isSunday);
            case ShiftBlockType.Saturday:
                return !!this.activeShiftSchedules.find(s => s.isWeekend || s.isSaturday);
            case ShiftBlockType.Sunday:
                return !!this.activeShiftSchedules.find(s => s.isWeekend || s.isSunday);
            default: 
                return false;
        }
    }

    get isTotalUosConfigured(): boolean {
        return WorkflowPageUtil.hasUosConfiguration(this.costCenter);
    }

    resetShiftBlockOptions(selectedShiftBlockOption = this.selectOptionDefaultValue){
        this.selectedShiftBlockOption = selectedShiftBlockOption;
        this.selectedBlockSize = '-';
        this.selectedStartTime = this.selectOptionDefaultValue;
        this.selectedEndTime = this.selectOptionDefaultValue;
        this.selectedClosedSchedule = false;
        this.selectedShiftBlockType = -1;
    }

    get shiftBlockEndHours() : StartHour[] {
        let noEndTime = { description: 'No end time', hour: null };
        let shiftBlockHoursWithNoEndTime = this.shiftBlockHours.concat(noEndTime);
        return shiftBlockHoursWithNoEndTime;
    }

    get shiftBlockStartHours() : StartHour[] {
        return this.shiftBlockHours;
    }

    get showFixedWhpuos(): boolean {
        return StaffingGridUtil.isGridTypeFixed(this.selectedGridType);
    }

    get showVariableWhpuos(): boolean {
        return StaffingGridUtil.isGridTypeVariable(this.selectedGridType);
    }
}


